import type { FilterOption } from '@/scopes/search/types'
import { useIntl } from '@/intl'
import { useBreakpoints } from '@/scopes/common/components/utils/breakpoints'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createSelectDestinationActions } from '@/scopes/search/search/domain/actions/selectDestinationActions'
import { useMultiSelectFilter } from '@/scopes/search/search/domain/hooks'
import { ArrivalAirportFilter } from '@/scopes/search/search/pages/SearchPage/components/FiltersLegacy/AirportFilter'
import { useSearchDependencies } from '@/scopes/search/search/pages/SearchPage/context'
import { space } from '@sunwebgroup/ui-components'
import { Accordion } from '@sunwebgroup/ui-components/uds'
import { TopTitleBar } from '@sunwebgroup/ui-components/uds/index'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'
import { createSearchFilterClickEvent } from '../../../../domain/analytics'
import * as DialogPrimitive from './DialogPrimitive'
import { DropdownMultiselect, useEditableDropdownMultiselect } from './DropdownMultiselect'
import { FormField, FormFieldControl, FormFieldLabel } from './FormField'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    margin-bottom: ${space('primitives_space_16')};

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const MobileConfirmButton = styled(DropdownMultiselect.ConfirmButton)`
  ${DropdownMultiselect.ConfirmButton.modNoShadow()}
`

function CountryFilter() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues } = useMultiSelectFilter('Country')
  const { isTablet } = useBreakpoints()
  const { filterSelectionQueryParamHandler } = useSearchDependencies()
  const selectDestinationActions = createSelectDestinationActions({ filterSelectionQueryParamHandler })

  const handleValueChange = React.useCallback((newSelectedValues: string[]) => {
    selectDestinationActions.setCountryFilterSelection(newSelectedValues)
  }, [])

  const handleEditFinish = React.useCallback(
    (newSelectedValues: string[]) => {
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'country',
          result: mapLocalSelectionToGA4Result(newSelectedValues, options),
        }),
      )
    },
    [sendEvent, options],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: handleValueChange,
    onEditFinish: handleEditFinish,
  })

  if (isEmpty(options))
    return null

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'destination_filter_country_label' })}</FormFieldLabel>
      <FormFieldControl>
        <DropdownMultiselect.Root
          disabled={isFetching}
          placeholder={intl.formatMessage({ id: 'destination_filter_country_placeholder' })}
          asDialog={!isTablet}
          {...multiselectDropdownProps}
        >
          <DropdownMultiselect.Selection>
            {options.map(({ value, name }) => (
              <DropdownMultiselect.SelectedValue key={value} value={value}>
                {name}
              </DropdownMultiselect.SelectedValue>
            ))}
          </DropdownMultiselect.Selection>
          {isTablet && (
            <DropdownMultiselect.Popover>
              <DropdownMultiselect.Options>
                {options.map(({ value, name, resultsCount }) => (
                  <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount}>
                    {name}
                  </DropdownMultiselect.CheckboxWithCounterOption>
                ))}
              </DropdownMultiselect.Options>
              <DropdownMultiselect.ConfirmButton>
                {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
              </DropdownMultiselect.ConfirmButton>
            </DropdownMultiselect.Popover>
          )}
          {!isTablet && (
            <DropdownMultiselect.Dialog>
              <DialogPrimitive.Root>
                <DialogPrimitive.Header>
                  <TopTitleBar.Root>
                    <TopTitleBar.CenterArea title={intl.formatMessage({ id: 'destination_filter_country_label' })} />
                    <TopTitleBar.RightArea>
                      <DropdownMultiselect.CrossButton />
                    </TopTitleBar.RightArea>
                  </TopTitleBar.Root>
                </DialogPrimitive.Header>
                <DialogPrimitive.Body>
                  <DropdownMultiselect.Options>
                    {options.map(({ value, name, resultsCount }) => (
                      <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount}>
                        {name}
                      </DropdownMultiselect.CheckboxWithCounterOption>
                    ))}
                  </DropdownMultiselect.Options>
                </DialogPrimitive.Body>
                <DialogPrimitive.Footer>
                  <DropdownMultiselect.Footer>
                    <MobileConfirmButton>
                      {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
                    </MobileConfirmButton>
                  </DropdownMultiselect.Footer>
                </DialogPrimitive.Footer>
              </DialogPrimitive.Root>
            </DropdownMultiselect.Dialog>
          )}
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}

function RegionFilter() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues } = useMultiSelectFilter('Region')
  const { isTablet } = useBreakpoints()
  const { filterSelectionQueryParamHandler } = useSearchDependencies()
  const selectDestinationActions = createSelectDestinationActions({ filterSelectionQueryParamHandler })

  const handleValueChange = React.useCallback((newSelectedValues: string[]) => {
    selectDestinationActions.setRegionFilterSelection(newSelectedValues)
  }, [])

  const handleEditFinish = React.useCallback(
    (newSelectedValues: string[]) => {
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'region',
          result: mapLocalSelectionToGA4Result(newSelectedValues, options),
        }),
      )
    },
    [sendEvent, options],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: handleValueChange,
    onEditFinish: handleEditFinish,
  })

  if (isEmpty(options))
    return null

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'destination_filter_region_label' })}</FormFieldLabel>
      <FormFieldControl>
        <DropdownMultiselect.Root
          disabled={isFetching}
          placeholder={intl.formatMessage({ id: 'destination_filter_region_placeholder' })}
          asDialog={!isTablet}
          {...multiselectDropdownProps}
        >
          <DropdownMultiselect.Selection>
            {options.map(({ value, name }) => (
              <DropdownMultiselect.SelectedValue key={value} value={value}>
                {name}
              </DropdownMultiselect.SelectedValue>
            ))}
          </DropdownMultiselect.Selection>
          {isTablet && (
            <DropdownMultiselect.Popover>
              <DropdownMultiselect.Options>
                {options.map(({ value, name, resultsCount }) => (
                  <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount}>
                    {name}
                  </DropdownMultiselect.CheckboxWithCounterOption>
                ))}
              </DropdownMultiselect.Options>
              <DropdownMultiselect.ConfirmButton>
                {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
              </DropdownMultiselect.ConfirmButton>
            </DropdownMultiselect.Popover>
          )}
          {!isTablet && (
            <DropdownMultiselect.Dialog>
              <DialogPrimitive.Root>
                <DialogPrimitive.Header>
                  <TopTitleBar.Root>
                    <TopTitleBar.CenterArea title={intl.formatMessage({ id: 'destination_filter_region_label' })} />
                    <TopTitleBar.RightArea>
                      <DropdownMultiselect.CrossButton />
                    </TopTitleBar.RightArea>
                  </TopTitleBar.Root>
                </DialogPrimitive.Header>
                <DialogPrimitive.Body>
                  <DropdownMultiselect.Options>
                    {options.map(({ value, name, resultsCount }) => (
                      <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount}>
                        {name}
                      </DropdownMultiselect.CheckboxWithCounterOption>
                    ))}
                  </DropdownMultiselect.Options>
                </DialogPrimitive.Body>
                <DialogPrimitive.Footer>
                  <DropdownMultiselect.Footer>
                    <MobileConfirmButton>
                      {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
                    </MobileConfirmButton>
                  </DropdownMultiselect.Footer>
                </DialogPrimitive.Footer>
              </DialogPrimitive.Root>
            </DropdownMultiselect.Dialog>
          )}
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}

function CityFilter() {
  const intl = useIntl()
  const { sendEvent } = useAnalytics()
  const { isFetching, options, selectedValues } = useMultiSelectFilter('City')
  const { isTablet } = useBreakpoints()
  const { filterSelectionQueryParamHandler } = useSearchDependencies()
  const selectDestinationActions = createSelectDestinationActions({ filterSelectionQueryParamHandler })

  const handleValueChange = React.useCallback((newSelectedValues: string[]) => {
    selectDestinationActions.setCityFilterSelection(newSelectedValues)
  }, [])

  const handleEditFinish = React.useCallback(
    (newSelectedValues: string[]) => {
      sendEvent(
        createSearchFilterClickEvent({
          element: 'checkbox',
          type: 'city',
          result: mapLocalSelectionToGA4Result(newSelectedValues, options),
        }),
      )
    },
    [sendEvent, options],
  )

  const multiselectDropdownProps = useEditableDropdownMultiselect({
    value: selectedValues,
    onValueChange: handleValueChange,
    onEditFinish: handleEditFinish,
  })

  if (isEmpty(options))
    return null

  return (
    <FormField as="div">
      <FormFieldLabel>{intl.formatMessage({ id: 'destination_filter_city_label' })}</FormFieldLabel>
      <FormFieldControl>
        <DropdownMultiselect.Root
          disabled={isFetching}
          placeholder={intl.formatMessage({ id: 'destination_filter_city_placeholder' })}
          asDialog={!isTablet}
          {...multiselectDropdownProps}
        >
          <DropdownMultiselect.Selection>
            {options.map(({ value, name }) => (
              <DropdownMultiselect.SelectedValue key={value} value={value}>
                {name}
              </DropdownMultiselect.SelectedValue>
            ))}
          </DropdownMultiselect.Selection>
          {isTablet && (
            <DropdownMultiselect.Popover>
              <DropdownMultiselect.Options>
                {options.map(({ value, name, resultsCount }) => (
                  <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount}>
                    {name}
                  </DropdownMultiselect.CheckboxWithCounterOption>
                ))}
              </DropdownMultiselect.Options>
              <DropdownMultiselect.ConfirmButton>
                {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
              </DropdownMultiselect.ConfirmButton>
            </DropdownMultiselect.Popover>
          )}
          {!isTablet && (
            <DropdownMultiselect.Dialog>
              <DialogPrimitive.Root>
                <DialogPrimitive.Header>
                  <TopTitleBar.Root>
                    <TopTitleBar.CenterArea title={intl.formatMessage({ id: 'destination_filter_city_label' })} />
                    <TopTitleBar.RightArea>
                      <DropdownMultiselect.CrossButton />
                    </TopTitleBar.RightArea>
                  </TopTitleBar.Root>
                </DialogPrimitive.Header>
                <DialogPrimitive.Body>
                  <DropdownMultiselect.Options>
                    {options.map(({ value, name, resultsCount }) => (
                      <DropdownMultiselect.CheckboxWithCounterOption key={value} value={value} count={resultsCount}>
                        {name}
                      </DropdownMultiselect.CheckboxWithCounterOption>
                    ))}
                  </DropdownMultiselect.Options>
                </DialogPrimitive.Body>
                <DialogPrimitive.Footer>
                  <DropdownMultiselect.Footer>
                    <MobileConfirmButton>
                      {intl.formatMessage({ id: 'destination_filter_confirm_button' })}
                    </MobileConfirmButton>
                  </DropdownMultiselect.Footer>
                </DialogPrimitive.Footer>
              </DialogPrimitive.Root>
            </DropdownMultiselect.Dialog>
          )}
        </DropdownMultiselect.Root>
      </FormFieldControl>
    </FormField>
  )
}

export function DestinationFilter() {
  const intl = useIntl()
  const { options: countries } = useMultiSelectFilter('Country')
  const { options: regions } = useMultiSelectFilter('Region')
  const { options: cities } = useMultiSelectFilter('City')
  const { options: arrivals } = useMultiSelectFilter('ArrivalAirport')

  const showWrapper = !isEmpty(countries) || !isEmpty(regions) || !isEmpty(cities) || !isEmpty(arrivals)

  return (
    showWrapper && (
      <Accordion.Item title={intl.formatMessage({ id: 'destination_filter_label' })} defaultOpen>
        <Wrapper>
          <CountryFilter />
          <RegionFilter />
          <CityFilter />
          <ArrivalAirportFilter />
        </Wrapper>
      </Accordion.Item>
    )
  )
}

function mapLocalSelectionToGA4Result(localSelection: string[], options: FilterOption<string>[]) {
  return options
    .filter(option => localSelection.includes(option.value))
    .map(option => option.name)
    .join(',')
}
